"use client"
import { useRouter } from "next/router";
import { useEffect, useReducer, useRef } from "react";
import LocationResultList from "src/components/locationresultlist/LocationResultList";
import { ISearchAction } from "src/components/search/ISearchAction";
import Search from "src/components/search/Search";
import SearchContentList from "src/components/search/SearchContentList";
import TopOfPageSponsor from "src/components/takoversponsor/TopOfPageSponsor";
import theme from "src/components/themes/DefaultTheme";
import { useAzLocationSearch, useAzSearch } from "src/lib/client/useSearch";
import { ISponsorProps } from "src/lib/server/sanity";
import styled from 'src/lib/styles/css';

export type CompleteSearchResultSponsorProps = Pick<ISponsorProps, "hasSponsor" | "sponsorClickUrl" | "sponsorTopImageDesktop" | "sponsorTopImageMobile">;

//#region [Props]
type CompleteSearchResultProps = {
    isStreamingOnly?: boolean;
    azShowScore?: boolean;
    azureSearchEnabled?: boolean;
    customSortNum?: number | string;
    maxNumItems?: number;
} & CompleteSearchResultSponsorProps;
//#endregion

//#region [Component]
export default function CompleteSearchResult({ isStreamingOnly = false, azShowScore = false, azureSearchEnabled = false, customSortNum, hasSponsor, sponsorClickUrl, sponsorTopImageDesktop, sponsorTopImageMobile, maxNumItems = 100 }: CompleteSearchResultProps) {
    const useAzureSearch = process.env.NEXT_PUBLIC_USE_AZURE_SEARCH === "true" || azureSearchEnabled;
    const router = useRouter();
    const [state, dispatch] = useReducer(searchReducer, { searchExpression: "", showSearchResults: false } as ISearchState)
    const _timeout = useRef<number | null>(null);

    useEffect(() => {
        if (router.query.q) {
            let q = "";
            if (Array.isArray(router.query.q)) {
                if ((router.query.q?.length ?? 0) > 0) {
                    q = router.query.q[0];
                }
            } else {
                q = router.query.q;
            }
            if (q && q !== "") {
                console.debug("Setting user query", q);
                dispatch({ type: "DO_SEARCH", searchExpression: q });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [router.query?.q]);

    useEffect(() => {
        // keep url updated
        if (_timeout.current) {
            clearTimeout(_timeout.current);
            _timeout.current = null;
        }
        if (state.searchExpression && typeof window !== "undefined") {
            _timeout.current = window.setTimeout(() => {
                console.debug("Updating url", state.searchExpression)
                router.replace(`${router.pathname}?q=${encodeURIComponent(state.searchExpression)}`, undefined, { shallow: true });
                _timeout.current = null;
            }, 400);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.searchExpression]);

    const { searchLoading: locationSearchLoading, searchResult: azLocationResult } = useAzLocationSearch(state.searchExpression,
        {
            active: !!state.searchExpression && !isStreamingOnly,
        }
    );

    const { searchLoaded, searchLoading: azSearchLoading, searchResult: azSearchResult } = useAzSearch(state.searchExpression, {
        streamingOnly: isStreamingOnly,
        maxNumItems: maxNumItems,
        active: !!state.searchExpression,
        customSortNum
    });

    const searchLoading = (useAzureSearch && azSearchLoading);

    const showLocationResult = !isStreamingOnly && (!locationSearchLoading && (azLocationResult?.results?.length ?? 0) > 0);

    return <div>
        <SSearchInputContainer>
            <SSearch
                dispatch={dispatch}
                searchExpression={state.searchExpression}
                showSearchResults={state.showSearchResults}
                placeholderText="Søk etter film eller serie"
                isSearching={searchLoading}
                controlled
                disableEnter
                className={(sponsorTopImageDesktop?.length ?? 0) > 0 ? "withSponsor" : undefined}
            />
            {hasSponsor && <STopOfPageSponsor desktopImages={sponsorTopImageDesktop} mobileImages={sponsorTopImageMobile} clickUrl={sponsorClickUrl} />}
        </SSearchInputContainer>
        {showLocationResult &&
            <SLocationResultList locationList={azLocationResult?.results}
                maxNumToShow={100}
                clickTracker={(locName) => trackHit(false, "Kinosted", locName, state.searchExpression)} />}
        {state.searchExpression?.trim() && <SearchContentList
            isSearching={searchLoading}
            contentList={azSearchResult?.results}
            itemClickTracker={(id, title) => trackHit(isStreamingOnly, title, id.toString(), state.searchExpression)}
            forceStreaming={isStreamingOnly}
            azShowScore={azShowScore}
            azureSearchEnabled={useAzureSearch}
        />}

    </div>;
}
//#endregion

//#region [Other]
interface ISearchState {
    showSearchResults: boolean;
    searchExpression: string;

}

function searchReducer(state: ISearchState, action: ISearchAction): ISearchState {
    switch (action.type) {
        /*
        case 'BEGIN_SEARCH':

            return { ...state, showSearchResults: true, hasFiredFirstFocus: true };
            */
        case 'END_SEARCH':
            return { ...state, showSearchResults: false };
        case 'CLEAR_SEARCH':
            return { ...state, showSearchResults: false, searchExpression: "" };
        case 'DO_SEARCH':
            return { ...state, searchExpression: (action.searchExpression ?? ""), showSearchResults: true };
        default:
            return state;
    }
}

function trackHit(streamingOnly: boolean, title: string, id: string, searchExpression: string) {
    window?.dataLayer?.push({
        'event': `fw_Search${streamingOnly ? '_sg' : ''}`,
        'eventCategory': `${streamingOnly ? 'SG ' : ''}Søk Treff`,
        'eventAction': `${title} | ${id}`,
        'eventLabel': searchExpression
    });
}
//#endregion

//#region [Styles]
const SLocationResultList = styled(LocationResultList)`
	max-width: 1580px;
`;

const SSearchInputContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    max-width: 1640px;
    margin: 0 auto;

    @media (min-width: 550px) {
        flex-wrap: nowrap;
    }
`;

const STopOfPageSponsor = styled(TopOfPageSponsor)`
    flex: 0 0 100%;

    @media (min-width: 550px) {
        flex: 0 1 auto;
        .topOfPageSponsorInnerContainer {
            margin: 0 !important;
        }
    }

`;

const SSearch = styled(Search)`
	max-width: 800px;
	padding-left: 0;
	margin: 1rem auto;

    &.withSponsor {
        margin-left: 0;
        padding-left: 10px;
    }

	.searchInput {
		border: 3px solid var(--textcolor);
		border-right: none;
		height: 60px;
		border-radius: 30px 0 0 30px;
		min-width: 60px;
		font-size: 20px;

		outline: var(--textcolor);

		&:focus::placeholder {
			opacity: 0.5;
		}
	}

	.searchButton {
		border: 3px solid var(--textcolor);
		border-left: none;
		height: 60px;
		border-radius: 0 30px 30px 0;
		width: 50px;
		color: ${theme.textColor};
		padding: 5px 15px 5px 4px;
		text-align: right;
		flex: 0 0 auto;
		cursor: pointer;
	}

`;

//#endregion